import { CustomFileUpload } from './CustomFileUpload';
import { Toast } from 'primereact/toast';
import { fetchAccessToken } from '../utils/common';
import { useAuth0 } from '@auth0/auth0-react';
import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Dialog } from 'primereact/dialog';
import { Checkbox } from 'primereact/checkbox';
import { ProgressSpinner } from 'primereact/progressspinner';
import { MappingForm } from './MappingForm';
import { Dropdown } from 'primereact/dropdown';
import { GroupingForm } from './GroupingForm';
import configSettings from '../configSettings.json';
import { Menu } from 'primereact/menu';
import { ProgressBar } from 'primereact/progressbar';
import { apiCallErrorHandler } from '../utils/common';

export const ConfigForm = (props) => {
    const { getAccessTokenSilently } = useAuth0();
    const navigate = useNavigate();
    useEffect(() => { document.title = 'OptiConfig Supplier File Upload'; }, []);

    const savedSessionId = props.savedSessionId;
    const [sessionId, setSessionId] = useState(savedSessionId || crypto.randomUUID());
    const [uploadedFiles, setUploadedFiles] = useState([]);
    const [token, setToken] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [fetching, setFetching] = useState(false);
    const [dialogData, setDialogData] = useState(null);
    const [batchTitle, setBatchTitle] = useState('');
    const [minGroup, setMinGroup] = useState(1);
    const [batchSupplierInfo, setBatchSupplierInfo] = useState('');
    const [comments, setComments] = useState('');
    const [batchTitleTouched, setBatchTitleTouched] = useState(false);
    const [batchSupplierInfoTouched, setBatchSupplierInfoTouched] = useState(false);
    const [worksheet, setWorksheet] = useState('');
    const [worksheetOptions, setWorksheetOptions] = useState([]);
    const [worksheetTouched, setWorksheetTouched] = useState(false);
    const [mappedData, setMappedData] = useState(null);
    const [enrichedData, setEnrichedData] = useState(null);
    const lastTop5 = useRef([]);
    const [enrichmentSource, setEnrichmentSource] = useState(null);
    const [marketStudyCountryDialogVisible, setMarketStudyCountryDialogVisible] = useState(false);
    const [marketStudySelectedCountries, setMarketStudySelectedCountries] = useState([]);
    const [pendingMarketStudySource, setPendingMarketStudySource] = useState(null);
    const marketStudyMarketOverride = useRef(null);
    const [groupCount, setGroupCount] = useState(0);
    const [market, setMarket] = useState(configSettings.DefaultMarket || '');
    const [language, setLanguage] = useState(configSettings.DefaultLanguage || '');
    const [vehiclesType, setVehiclesType] = useState('Electrical');
    const [groupBy, setGroupBy] = useState([]);
    const [doneCount, setDoneCount] = useState(0);
    const [isRunningAll, setIsRunningAll] = useState(false);
    const [worksheetFixed, setWorksheetFixed] = useState(false);
    const [filePlaceHolder, setFilePlaceHolder] = useState('');
    const toast = useRef(null);
    const mappingFormRef = useRef(null);
    const enrichMenu = useRef(null);

    const showError = (err) => {
        if (toast.current) {
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: err,
                life: 3000
            });
        }

        console.warn('error', err);
    }

    const showSuccess = (msg, life = 3000) => {
        if (toast.current) {
            toast.current.show({
                severity: 'success',
                summary: 'Success',
                detail: msg,
                life: life
            });
        }

        console.log('success', msg);
    };

    const isReadOnly = savedSessionId != null;

    useEffect(() => {
        const fetchToken = async () => {
            try {
                const accessToken = await fetchAccessToken(getAccessTokenSilently)
                setToken(accessToken);
            } catch (error) {
                console.error('Error fetching access token:', error);
                showError('Failed to fetch access token. Please try again.');
            }
        };
        fetchToken();
    }, [getAccessTokenSilently]);

    const withSpinner = async (fn) => {
        setSubmitting(true);
        try {
            return await fn();
        } finally {
            setSubmitting(false);
        }
    };

    useEffect(() => {
        if (!isReadOnly && uploadedFiles.length === 0) {
            const newSessionId = crypto.randomUUID();
            setWorksheetFixed(false);
            setSessionId(newSessionId);
            // console.log('New session ID generated:', newSessionId);
        }
    }, [uploadedFiles, isReadOnly]);

    const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

    const fetchSession = async () => {
        setSessionId(savedSessionId);
        await withSpinner(async () => {
            try {
                setFetching(true);
                const response = await fetch(
                    `${process.env.REACT_APP_API_URL || ''}/file/load-session/${savedSessionId}`,
                    {
                        method: 'GET',
                        headers: {
                            'Content-Type': 'application/json',
                            Authorization: `Bearer ${token}`
                        }
                    }
                );
                if (!response.ok) await apiCallErrorHandler(response, 'Failed to load session');
                const data = await response.json();

                if (!data || !data.sessionData) {
                    showError('No session data found');
                    return;
                }

                const { request, mapping, grouping, enrichedRowCount } = data.sessionData;
                const source = data.sessionData.enrichmentSource;

                if (request) {
                    setBatchTitle(request.batchTitle || '');
                    setBatchSupplierInfo(request.batchSupplierInfo || '');
                    setComments(request.comments || '');
                    setMinGroup(request.minGroup || 1);
                    setWorksheet(request.worksheet || '');
                    setFilePlaceHolder(request.fileName || '');
                    setMarket(request.market || configSettings.DefaultMarket || '');
                    setLanguage(request.language || configSettings.DefaultLanguage || '');
                    setVehiclesType(request.vehiclesType || '');

                    setWorksheetOptions([
                        { label: request.worksheet, value: request.worksheet }
                    ]);
                    setWorksheetFixed(true);
                }

                setDialogData(data.processFileData);

                const savedData = {
                    fileName: request.fileName,
                    grouping: grouping,
                    enrichmentSource: source,
                    worksheet: request.worksheet
                };

                setEnrichmentSource(source);

                if (request != null && mapping != null && Object.keys(mapping).length > 0) {
                    await requestMappedData(mapping, savedData);
                };

                if (request != null && grouping != null && Object.keys(grouping).length > 0) {
                    setGroupBy(grouping);
                }

                if (request != null && grouping != null && enrichedRowCount && source) {
                    await delay(1000);
                    await handleEnrichment(source, savedData);
                }

            } catch (error) {
                showError(error.message || 'Failed to load session');
            } finally {
                setFetching(false);
            }
        });
    };

    useEffect(() => {
        if (!savedSessionId || !token) return;

        fetchSession();
        // eslint-disable-next-line
    }, [savedSessionId, token]);

    const marketOptions = (configSettings.Markets || []).map(m => ({ label: m, value: m }));
    const languageOptions = (configSettings.Languages || []).map(l => ({ label: l, value: l }));
    const vehiclesTypeOptions = [
        { label: "Standard", value: "Standard" },
        { label: "Standard and Electrical", value: "Electrical" },
        { label: "Utility Cars", value: "UtilityCars" },
        { label: "Luxury Cars", value: "LuxuryCars" }
    ];

    const onStart = async (e) => {
        e.preventDefault();

        if (!isReadOnly && (!uploadedFiles || uploadedFiles.length !== 1)) {
            showError('Please upload at least one Excel file');
            return;
        }
        if (!worksheet.trim()) {
            setWorksheetTouched(true);
            showError('Please select a worksheet');
            return;
        }
        if (!market) {
            showError('Please select a market');
            return;
        }
        if (!language) {
            showError('Please select a language');
            return;
        }

        setWorksheetFixed(true);

        await withSpinner(async () => {
            const fileNames = uploadedFiles.map(file => file.name);
            const fileData = uploadedFiles.map(file => ({
                _relativePath: file._relativePath,
                lastModified: file.lastModified,
                lastModifiedDate: file.lastModifiedDate,
                name: file.name,
                size: file.size,
                type: file.type,
                webkitRelativePath: file.webkitRelativePath
            }));

            const payload = {
                fileName: filePlaceHolder || fileNames[0],
                worksheet,
                batchTitle,
                batchSupplierInfo,
                comments,
                market,
                language,
                vehiclesType,
                minGroup,
                uploadedFiles: fileData
            };

            try {
                const response = await fetch(
                    `${process.env.REACT_APP_API_URL || ''}/file/process-file/${sessionId}`,
                    {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            Authorization: `Bearer ${token}`
                        },
                        body: JSON.stringify(payload)
                    }
                );

                if (!response.ok) await apiCallErrorHandler(response, 'Failed to process file');

                const data = await response.json();

                const hasSuggestedMapping = data.suggestedMapping && Object.keys(data.suggestedMapping).length > 0;
                const hasSuggestedGrouping = data.suggestedGrouping && data.suggestedGrouping.length > 0;

                if (hasSuggestedMapping) {
                    try {
                        await doMapData(data.suggestedMapping);
                        showSuccess('This file format is already known — mapping and grouping will be applied automatically.');
                        if (hasSuggestedGrouping) {
                            setGroupBy(data.suggestedGrouping);
                        }
                    } catch {
                        if (toast.current) {
                            toast.current.show({
                                severity: 'error',
                                summary: 'Error',
                                detail: 'Suggested mapping could not be applied — please configure mapping manually.',
                                sticky: false
                            });
                        }
                    }
                } else if (hasSuggestedGrouping) {
                    showSuccess('This file format is already known — grouping will be applied automatically.');
                    setGroupBy(data.suggestedGrouping);
                }

                setDialogData(data);
            } catch (error) {
                showError(error.message || 'File processing failed');
            }
        });
    };

    const doMapData = async (mapping, savedData = null) => {
        const response = await fetch(
            `${process.env.REACT_APP_API_URL || ''}/file/map-data/${sessionId}`,
            {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify({
                    fileName: savedData?.fileName || filePlaceHolder || uploadedFiles[0].name,
                    worksheet: savedData?.worksheet || worksheet,
                    mapping
                })
            }
        );

        if (!response.ok) await apiCallErrorHandler(response, 'Failed to request grouping data');

        let data = await response.json();
        data.mapping = mapping;
        setMappedData(data);
    };

    const requestMappedData = async (mapping, savedData = null) => {
        if (!mapping || Object.keys(mapping).length === 0) {
            showError('Please complete the mapping before proceeding.');
            return;
        }

        await withSpinner(async () => {
            try {
                await doMapData(mapping, savedData);
            } catch (error) {
                showError(error.message || 'Grouping request failed');
            }
        });
    }
    const groupingFormRef = useRef(null);

    const dialogHeader = () => {
        if (!dialogData) return null;

        const handleCopySessionLink = () => {
            const url = `${window.location.origin}/config/${sessionId}`;
            navigator.clipboard.writeText(url);
            showSuccess('Session link copied to clipboard');
        };

        return (
            <div
                style={{
                    fontSize: '1rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 16,
                    justifyContent: 'center',
                    width: '100%',
                }}
            >
                {enrichedData ? (
                    <span>
                        {(enrichmentSource?.toUpperCase() === "MYRENAULTQUEUE" || enrichmentSource?.toUpperCase() === "PARTSLINKQUEUE" || enrichmentSource?.toUpperCase() === "MARKETSTUDYQUEUE" || enrichmentSource?.toUpperCase() === "MARKETSTUDYLCQUEUE") 
                            ? `VINs queued with ${enrichmentSource?.toUpperCase().replace("QUEUE", "")}`
                            : `Data enriched with ${enrichmentSource?.toUpperCase()}: ${enrichedData.allEnrichedRows.length} of ${dialogData.rowCount} shown`
                        }
                    </span>
                ) : mappedData?.mapping ? (
                    (() => {
                        const mapping = mappedData.mapping;
                        const totalTargetColumnCount = Object.keys(mapping).length;
                        const mappedCount = Object.values(mapping).filter(Boolean).length;
                        return (
                            <span>
                                Total Rows: {dialogData.rowCount} &nbsp;|&nbsp;
                                Mapped Columns: {mappedCount} / {totalTargetColumnCount}
                            </span>
                        );
                    })()
                ) : (
                    <span>
                        Total Rows: {dialogData.rowCount} &nbsp;|&nbsp;
                        Total Columns: {dialogData.totalColumnCount} &nbsp;|&nbsp;
                        Non-empty Columns: {dialogData.nonEmptyColumnCount}
                    </span>
                )}
                <Button
                    label="Copy session link"
                    icon="pi pi-copy"
                    className="p-button-sm p-button-secondary"
                    style={{ marginLeft: 8, height: 25 }}
                    onClick={handleCopySessionLink}
                />
            </div>
        );
    }

    const downloadAbu = async (mapping, grouping, datRows) => {
        await withSpinner(async () => {
            try {
                const response = await fetch(
                    `${process.env.REACT_APP_API_URL || ''}/file/download-abu/${sessionId}`,
                    {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            Authorization: `Bearer ${token}`
                        },
                        body: JSON.stringify({
                            fileName: filePlaceHolder || uploadedFiles[0].name,
                            worksheet: worksheet,
                            mapping,
                            grouping,
                            datRows,
                            market,
                            source: enrichmentSource ?? "SUPPLIER"
                        })
                    }
                );

                if (!response.ok) await apiCallErrorHandler(response, 'Failed to download ABU file');

                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `batch_${batchTitle}_abu.xlsx`;
                document.body.appendChild(a);
                a.click();
                a.remove();
                window.URL.revokeObjectURL(url);
            } catch (err) {
                showError('ABU Download failed: ' + err.message);
            }
        });
    }

    const downloadMapped = async (mapping, grouping, datRows, overrideEquipment, orderedColumnList) => {
        await withSpinner(async () => {
            try {
                const response = await fetch(
                    `${process.env.REACT_APP_API_URL || ''}/file/download-mapped/${sessionId}`,
                    {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            Authorization: `Bearer ${token}`
                        },
                        body: JSON.stringify({
                            fileName: filePlaceHolder || uploadedFiles[0].name,
                            worksheet: worksheet,
                            mapping,
                            grouping,
                            datRows,
                            overrideEquipment,
                            orderedColumnList,
                            source: enrichmentSource ?? "SUPPLIER"
                        })
                    }
                );

                if (!response.ok) await apiCallErrorHandler(response, 'Failed to download file');

                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;

                let suffix = "mapped";
                if (datRows) {
                    suffix = "full";
                } else if (grouping) {
                    suffix = "sample";
                }

                a.download = `batch_${batchTitle}_${suffix}.xlsx`;
                document.body.appendChild(a);
                a.click();
                a.remove();
                window.URL.revokeObjectURL(url);
            } catch (err) {
                showError('Download failed: ' + err.message);
            }
        });
    }

    const requesTop5Enrichment = async (grouping, top5, source, savedData = null, updatedMapping = null, isRunAll = false, marketOverride = null) => {
        if (savedData)
            grouping = savedData.grouping || grouping;

        if (!grouping || Object.keys(grouping).length === 0) {
            showError('Please select at Group By columns before proceeding');
            return false;
        }

        setEnrichmentSource(source);

        return await withSpinner(async () => {
            try {
                const mapping = mappingFormRef.current ? mappingFormRef.current.getMapping() : (updatedMapping ?? {});

                lastTop5.current = top5;

                const response = await fetch(
                    `${process.env.REACT_APP_API_URL || ''}/file/enrich-top/${sessionId}`,
                    {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            Authorization: `Bearer ${token}`
                        },
                        body: JSON.stringify({
                            fileName: savedData?.fileName || filePlaceHolder || uploadedFiles[0].name,
                            worksheet,
                            mapping,
                            grouping: grouping,
                            top5,
                            market: marketOverride ?? market,
                            language,
                            simplifiedEquipment: true,
                            source,
                            isRunAll
                        })
                    }
                );

                if (!response.ok) await apiCallErrorHandler(response, 'Failed to request sample enrichment');

                let data = await response.json();

                const {
                    preview,
                    allEnriched,
                    allEnrichedRows,
                    allGroups,
                    columns,
                    priorities,
                    currentTop5
                } = data;

                if (top5.length === 0) {
                    setDoneCount(currentTop5.length);
                    // console.log(currentTop5.length, allGroups.length);
                    lastTop5.current = currentTop5;
                }

                setGroupCount(allGroups.length);

                setEnrichedData({
                    allEnriched,
                    allEnrichedRows,
                    allGroups,
                    columns,
                    priorities,

                    preview,
                    mapping: data.mapping,
                    grouping,
                    enrichmentSource: source.toUpperCase(),
                });
                return true;
            } catch (error) {
                showError(error.message || 'Sample enrichment failed');
                console.error('Enrichment error:', error);
                return false;
            }
        });
    }

    const handleEnrichment = async (source, savedData = null, marketOverride = null) => {
        const result = await requesTop5Enrichment(groupBy, [], source, savedData, null, false, marketOverride);

        // console.log(source);
        if (result && (source.toUpperCase() === "MYRENAULTQUEUE" || source.toUpperCase() === "PARTSLINKQUEUE" || source.toUpperCase() === "MARKETSTUDYQUEUE" || source.toUpperCase() === "MARKETSTUDYLCQUEUE")) {
            showSuccess("VINs have been queued successfully. Please wait until processed (~ 20min / 20 VINs)", 15000);
        }
    };

    const MARKET_STUDY_SOURCES = ['MarketStudyQueue', 'MarketStudyLCQueue'];

    // Display-only label helper: renders the country name as-is, followed by the source
    // (existing "(...)" suffix, or "AutoScout24" when none is present) in a smaller grey
    // font without brackets. The underlying value sent to the API is untouched.
    const renderMarketStudyCountryLabel = (country) => {
        if (!country) return country;
        const match = country.match(/^(.*?)\s*\(([^)]*)\)\s*$/);
        const base = match ? match[1] : country;
        const suffix = match ? match[2] : 'AutoScout24';
        return (
            <>
                <strong>{base}</strong> <span style={{ fontSize: '0.85em', color: '#888' }}>{suffix}</span>
            </>
        );
    };

    const openMarketStudyCountryDialog = (source) => {
        setPendingMarketStudySource(source);
        setMarketStudySelectedCountries([]);
        setMarketStudyCountryDialogVisible(true);
    };

    const confirmMarketStudyCountries = async () => {
        if (marketStudySelectedCountries.length === 0) {
            showError('Please select at least one country');
            return;
        }

        const countriesMarket = marketStudySelectedCountries.join('|');
        marketStudyMarketOverride.current = countriesMarket;
        setMarketStudyCountryDialogVisible(false);

        await handleEnrichment(pendingMarketStudySource, null, countriesMarket);
    };

    const runAllEnrichment = async () => {
        setIsRunningAll(true);

        function chunkArray(array, size) {
            const chunks = [];
            for (let i = 0; i < array.length; i += size) {
                chunks.push(array.slice(i, i + size));
            }
            return chunks;
        }

        let localDoneCount = 0;

        async function processGroups() {
            const chunks = chunkArray(enrichedData.allGroups, 5);

            const marketOverride = MARKET_STUDY_SOURCES.some(s => s.toUpperCase() === enrichmentSource?.toUpperCase())
                ? marketStudyMarketOverride.current
                : null;

            for (const groupChunk of chunks) {

                const result = await requesTop5Enrichment(groupBy, groupChunk, enrichmentSource, null, null, localDoneCount > 0, marketOverride);

                if (!result) return false;

                localDoneCount += groupChunk.length;
                setDoneCount(localDoneCount);
            }

            return true;
        }

        const result = await processGroups();

        setIsRunningAll(false);

        // console.log(enrichmentSource);
        if (result && (enrichmentSource.toUpperCase() === "MYRENAULTQUEUE" || enrichmentSource.toUpperCase() === "PARTSLINKQUEUE" || enrichmentSource.toUpperCase() === "MARKETSTUDYQUEUE" || enrichmentSource.toUpperCase() === "MARKETSTUDYLCQUEUE")) {
            showSuccess("All sample VINs have been queued successfully. Please wait until processed (~ 20min / 20 VINs)", 15000);
        }
    };

    const enrichItems = [
        {
            label: 'Enrich with DAT',
            command: () => handleEnrichment('Dat')
        },
        {
            label: 'Enrich with Outvin',
            command: () => handleEnrichment('Outvin')
        },
        // {
        //     label: 'Enrich with Jato',
        //     command: () => handleEnrichment('Jato')
        // },
        {
            label: 'Enrich with Vincario',
            command: () => handleEnrichment('Vincario')
        },
        {
            label: 'Queue with Partslink',
            command: () => handleEnrichment('PartslinkQueue')
        },
        {
            label: 'Enrich with Partslink',
            command: () => handleEnrichment('Partslink')
        },
        {
            label: 'Queue with MyRenault',
            command: () => handleEnrichment('MyRenaultQueue')
        },
        {
            label: 'Enrich with MyRenault',
            command: () => handleEnrichment('MyRenault')
        },
        {
            label: 'Queue for Market Study',
            command: () => openMarketStudyCountryDialog('MarketStudyQueue')
        },
        // {
        //     label: 'Enrich with Autobiz',
        //     command: () => handleEnrichment('Autobiz')
        // },
        {
            label: 'Enrich with ChatGPT',
            command: () => handleEnrichment('ChatGpt')
        },
        {
            label: 'Enrich with Claude',
            command: () => handleEnrichment('Claude')
        },
        {
            label: 'Enrich Supplier Data',
            command: () => handleEnrichment('Supplier')
        },
        {
            label: 'Enrich with Supplier Codes',
            command: () => handleEnrichment('SupplierCodes')
        },
        {
            label: 'Enrich with TrackSights',
            command: () => handleEnrichment('TrackSights')
        }
    ];

    // True when every group already has a persisted enrichment record for the
    // currently selected source (not just any source), so there's nothing left to continue.
    const allRowsHaveRecords = (() => {
        if (!enrichedData || !enrichmentSource || !groupCount) return false;

        const columns = enrichedData.columns || [];
        const fromSourceIdx = columns.indexOf('FromSource');
        const groupVinIdx = columns.indexOf('GroupVin');
        if (fromSourceIdx === -1 || groupVinIdx === -1) return false;

        const sourceUpper = enrichmentSource.toUpperCase();
        const enrichedGroupVins = new Set(
            (enrichedData.allEnriched || [])
                .filter(row => (row[fromSourceIdx] || '').toUpperCase() === sourceUpper)
                .map(row => row[groupVinIdx])
        );

        return enrichedGroupVins.size >= groupCount;
    })();

    const applyDefaultPriorities = async () => {
        await withSpinner(async () => {
            try {
                const response = await fetch(
                    `${process.env.REACT_APP_API_URL || ''}/file/default-priorities/${sessionId}`,
                    {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            Authorization: `Bearer ${token}`
                        },
                        body: JSON.stringify({}) // no payload needed per spec
                    }
                );

                if (!response.ok) await apiCallErrorHandler(response, 'Failed to apply default priorities');
                showSuccess('Default priorities applied');
            } catch (err) {
                showError('Failed to apply default priorities: ' + (err?.message || 'Unknown error'));
            }
        });
    };

    return (
        <>
            <Toast
                ref={toast}
                appendTo={document.body}
                className="config-form-toast"
                style={{ zIndex: 110000 }}
                baseZIndex={110000}
                position="top-right"
            />
            <form className="grid" style={{ maxWidth: '100vw', overflowX: 'auto' }}>
                <div className="col-12">
                    <div className="grid formgrid p-fluid">
                        <div className="field col-2">
                            <div>
                                <Button
                                    type="button"
                                    icon="pi pi-list"
                                    label="Session list"
                                    style={{maxWidth: 200}}
                                    className="p-button-sm p-button-text"
                                    onClick={() => navigate('/')}
                                />
                            </div>
                        </div>
                        <div className="field col-8">
                            {!isReadOnly
                                ? <h4 style={{ textAlign: 'center' }}>Upload .xlsx file with VINs to analyze</h4>
                                : <h4 style={{ textAlign: 'center' }}>Continue previous session</h4>
                            }
                        </div>
                        <div className="col-2"></div>
                        <div className="col-3"></div>
                        <div className="field col-6">
                            {!isReadOnly ? (
                                <CustomFileUpload
                                    allowMultiple={false}
                                    acceptedFileTypes={[
                                        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                                        'application/vnd.ms-excel',
                                        '.xlsx',
                                        '.xls'
                                    ]}
                                    onMessage={(msg) => { showError(msg.detail); }}
                                    token={token}
                                    fileValue={uploadedFiles}
                                    sessionId={sessionId}
                                    onChange={(value) => {
                                        const file = JSON.parse(value);

                                        if (
                                            file.extraInfo &&
                                            Array.isArray(file.extraInfo)
                                        ) {
                                            const wsArr = file.extraInfo;

                                            setWorksheetOptions(wsArr.map(ws => ({
                                                label: ws,
                                                value: ws
                                            })));

                                            if (wsArr.length === 1) {
                                                setWorksheet(wsArr[0]);
                                            } else {
                                                setWorksheet('');
                                            }
                                        } else {
                                            setWorksheet('');
                                        }
                                    }}
                                    onChangeFiles={(files) => {
                                        // console.log(files);

                                        if (savedSessionId) return;

                                        setUploadedFiles(files);

                                        if (files.length === 0) {
                                            setWorksheet('');
                                            setWorksheetOptions([]);
                                        } else if (!batchTitle.trim()) {
                                            const fileName = files[0].name;
                                            const nameWithoutExt = fileName.replace(/\.[^./]+$/, '');
                                            setBatchTitle(nameWithoutExt);
                                        }
                                    }}
                                />
                            ) : (
                                <>
                                    <label>Uploaded File:</label>
                                    <InputText
                                        value={filePlaceHolder}
                                        readOnly
                                        className="w-full"
                                    />
                                </>
                            )}
                        </div>
                        <div className="col-3"></div>

                        {/* Worksheet */}
                        <div className="col-3"></div>
                        <div className={`field col-6${worksheetTouched && !worksheet ? ' p-invalid' : ''}`}>
                            <label htmlFor="worksheet">Worksheet</label>
                            <Dropdown
                                id="worksheet"
                                value={worksheet}
                                options={worksheetOptions}
                                onChange={e => setWorksheet(e.value)}
                                onBlur={() => setWorksheetTouched(true)}
                                className={`w-full${worksheetTouched && !worksheet ? ' p-invalid' : ''}`}
                                placeholder="Select worksheet"
                                disabled={worksheetOptions.length === 1 || worksheetFixed}
                            />
                            {worksheetTouched && !worksheet && (
                                <small className="p-error">Worksheet is required.</small>
                            )}
                        </div>
                        <div className="col-3"></div>

                        {/* Batch Title */}
                        <div className="col-3"></div>
                        <div className={`field col-6${batchTitleTouched && !batchTitle.trim() ? ' p-invalid' : ''}`}>
                            <label htmlFor="batchTitle">Batch Title</label>
                            <InputText
                                id="batchTitle"
                                value={batchTitle}
                                onChange={e => setBatchTitle(e.target.value)}
                                onBlur={() => setBatchTitleTouched(true)}
                                className={`w-full${batchTitleTouched && !batchTitle.trim() ? ' p-invalid' : ''}`}
                                placeholder="Enter batch title"
                                readOnly={isReadOnly}
                            />
                            {batchTitleTouched && !batchTitle.trim() && (
                                <small className="p-error">Batch Title is required.</small>
                            )}
                        </div>
                        <div className="col-3"></div>

                        {/* Batch Supplier Info */}
                        <div className="col-3"></div>
                        <div className={`field col-6${batchSupplierInfoTouched && !batchSupplierInfo.trim() ? ' p-invalid' : ''}`}>
                            <label htmlFor="batchSupplierInfo">Batch Supplier Info</label>
                            <InputText
                                id="batchSupplierInfo"
                                value={batchSupplierInfo}
                                onChange={e => setBatchSupplierInfo(e.target.value)}
                                onBlur={() => setBatchSupplierInfoTouched(true)}
                                className={`w-full${batchSupplierInfoTouched && !batchSupplierInfo.trim() ? ' p-invalid' : ''}`}
                                placeholder="Enter supplier info"
                                readOnly={isReadOnly}
                            />
                            {batchSupplierInfoTouched && !batchSupplierInfo.trim() && (
                                <small className="p-error">Batch Supplier Info is required.</small>
                            )}
                        </div>
                        <div className="col-3"></div>

                        {/* Market and Language Dropdowns */}
                        <div className="col-3"></div>
                        <div className="field col-3">
                            <label htmlFor="market">Market</label>
                            <Dropdown
                                id="market"
                                value={market}
                                options={marketOptions}
                                onChange={e => setMarket(e.value)}
                                placeholder="Select market"
                                className="w-full"
                                disabled={isReadOnly}
                            />
                        </div>
                        <div className="field col-3">
                            <label htmlFor="language">Language</label>
                            <Dropdown
                                id="language"
                                value={language}
                                options={languageOptions}
                                onChange={e => setLanguage(e.value)}
                                placeholder="Select language"
                                className="w-full"
                                disabled={isReadOnly}
                            />
                        </div>
                        <div className="col-3"></div>

                        {/* Vehicles Type */}
                        <div className="col-3"></div>
                        <div className="field col-6">
                            <label htmlFor="vehiclesType">Vehicles Type</label>
                            <Dropdown
                                id="vehiclesType"
                                value={vehiclesType}
                                options={vehiclesTypeOptions}
                                onChange={e => setVehiclesType(e.value)}
                                placeholder="Select vehicles type"
                                className="w-full"
                                disabled={isReadOnly}
                            />
                        </div>
                        <div className="col-3"></div>

                        {/* Comments */}
                        <div className="col-3"></div>
                        <div className="field col-6">
                            <label htmlFor="comments">Comments</label>
                            <InputText
                                id="comments"
                                value={comments}
                                onChange={e => setComments(e.target.value)}
                                className="w-full"
                                placeholder={!isReadOnly ? "Enter comments" : ""}
                                readOnly={isReadOnly}
                            />
                        </div>
                        <div className="col-3"></div>

                        <div className="col-3"></div>
                        <div className="field col-3">
                            <label htmlFor="comments">Sample Group Minimum Rows</label>
                            <InputText
                                id="minGroup"
                                value={minGroup}
                                min={1}
                                type="number"
                                onChange={e => setMinGroup(e.target.value)}
                                className="w-full"
                                placeholder="Enter minimum rows for group (1 by default)"
                                readOnly={isReadOnly}
                            />
                        </div>
                        <div className="col-6"></div>

                        <div className="col-3"></div>
                        <div className="field col-6" style={{ display: 'flex', justifyContent: 'center' }}>
                            <Button
                                label={!isReadOnly ? "Start" : "Continue"}
                                className="p-button-primary"
                                style={{ maxWidth: '300px', width: '100%' }}
                                disabled={
                                    (uploadedFiles.length === 0 && !isReadOnly) ||
                                    submitting ||
                                    !worksheet ||
                                    !batchTitle.trim() ||
                                    !batchSupplierInfo.trim()
                                }
                                onClick={onStart}
                            />
                        </div>
                    </div>
                </div>
            </form>
            <Dialog
                visible={marketStudyCountryDialogVisible}
                closable={true}
                modal
                baseZIndex={9000}
                header="Select countries for Market Study"
                style={{ width: '30rem' }}
                onHide={() => setMarketStudyCountryDialogVisible(false)}
                footer={
                    <div>
                        <Button label="Cancel" className="p-button-text" onClick={() => setMarketStudyCountryDialogVisible(false)} />
                        <Button label="Confirm" className="p-button-primary" onClick={confirmMarketStudyCountries} />
                    </div>
                }
            >
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                    {[...(configSettings.MarketStudyCountries || [])]
                        .sort((a, b) => a.localeCompare(b))
                        .map(country => (
                        <div key={country} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <Checkbox
                                inputId={`market-study-country-${country}`}
                                value={country}
                                checked={marketStudySelectedCountries.includes(country)}
                                onChange={e => {
                                    setMarketStudySelectedCountries(prev =>
                                        e.checked ? [...prev, country] : prev.filter(c => c !== country)
                                    );
                                }}
                            />
                            <label htmlFor={`market-study-country-${country}`}>{renderMarketStudyCountryLabel(country)}</label>
                        </div>
                    ))}
                </div>
            </Dialog>
            <Dialog
                visible={submitting}
                closable={false}
                modal
                baseZIndex={9000}
                header={null}
                style={{ width: 'auto', textAlign: 'center' }}
                contentStyle={{ padding: '2rem' }}
                footer={null}
            >
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    {isRunningAll ? (
                        <>
                            <ProgressBar
                                value={groupCount === 0 ? 0 : Math.round((doneCount / groupCount) * 100)}
                                showValue={false}
                                style={{ height: 12, width: 300 }}
                            />
                            <span style={{ marginTop: '1rem' }}>
                                {doneCount} / {groupCount} samples enriched
                            </span>
                        </>
                    ) : (
                        <>
                            <ProgressSpinner />
                            <span style={{ marginTop: '1rem' }}>Processing, please wait...</span>
                        </>
                    )}
                </div>
            </Dialog>
            <Dialog
                visible={dialogData !== null}
                closable={true}
                baseZIndex={100}
                onHide={() => {
                    setDialogData(null);
                    setMappedData(null);
                    setEnrichedData(null);
                    setGroupBy([]);
                    setEnrichmentSource(null);
                }}
                modal
                header={() => dialogHeader()}
                style={{ width: '95rem', height: '95vh', textAlign: 'center' }}
                contentStyle={{ padding: '1rem', paddingTop: 0, overflowY: 'hidden' }}
                headerStyle={{ padding: '0.5rem 0.5rem' }}
                footer={null}
            >
                <div className='grid' style={{ height: '90%' }}>
                    <div className='col-12' style={{ height: '100%' }}>
                        <div className='col-12' style={{ height: '100%', overflow: 'auto', display: 'flex', flexDirection: 'column', justifyContent: 'stretch' }}>
                            {(!mappedData && !fetching) && <MappingForm
                                ref={mappingFormRef}
                                data={dialogData}
                                onDownload={async (mapping) => {
                                    await downloadMapped(mapping);
                                }}
                            />}
                            {(mappedData && !enrichedData && !fetching) && <GroupingForm
                                ref={groupingFormRef}
                                data={mappedData}
                                groupBy={groupBy}
                                setGroupBy={setGroupBy}
                                onDownload={async (grouping) => {
                                    await downloadMapped(mappedData.mapping, grouping);
                                }}
                            />}
                            {enrichedData && <MappingForm
                                ref={mappingFormRef}
                                data={enrichedData}
                                onDownload={async (datMapping, datRows, overrideEquipment, columnList) => {                                    
                                    await downloadMapped(datMapping, enrichedData.grouping, datRows, overrideEquipment, columnList);
                                }}
                                onDownloadAbu={async (datMapping, datRows) => {
                                    await downloadAbu(datMapping, enrichedData.grouping, datRows);
                                }}
                                onSavePriorities={async (column, priorityDict) => {
                                    try {
                                        const response = await fetch(
                                            `${process.env.REACT_APP_API_URL || ''}/file/update-priorities/${encodeURIComponent(column)}/${sessionId}`,
                                            {
                                                method: 'POST',
                                                headers: {
                                                    'Content-Type': 'application/json',
                                                    Authorization: `Bearer ${token}`
                                                },
                                                body: JSON.stringify(priorityDict)
                                            }
                                        );

                                        if (!response.ok) await apiCallErrorHandler(response, 'Failed to update priorities');
                                        // Do not re-enrich here. MappingForm will trigger one enrich-top after all updates.
                                    } catch (err) {
                                        showError('Failed to update priorities');
                                        console.error('Failed to update priorities:', err);
                                        throw err; // let caller handle aggregate failures
                                    }
                                }}
                                onReload={async (currentMapping) => {
                                    await requesTop5Enrichment(groupBy, lastTop5.current, enrichmentSource, null, currentMapping);
                                }}
                                onApplyDefaultPriorities={applyDefaultPriorities} // <-- added
                            />}
                        </div>
                    </div>
                    {fetching 
                        ? <><div className='col-12'><ProgressBar mode="indeterminate" style={{ height: '6px' }}></ProgressBar></div></>
                        : <>
                            {(enrichedData && groupCount !== doneCount && !allRowsHaveRecords) 
                                ? <div className='col-4'></div>
                                : <div className='col-6'></div>}
                            
                            <div className='col-2'>
                                {mappedData && <Button
                                    label="Previous"
                                    className="p-button-secondary"
                                    onClick={() => {
                                        if (enrichedData)
                                            setEnrichedData(null);
                                        else {
                                            const actualMapping = mappedData.mapping;
                                            const ddata = dialogData;
                                            ddata.mapping = actualMapping;
                                            setDialogData(ddata);
                                            setMappedData(null);
                                        }
                                    }}
                                    style={{ marginTop: '1rem', width: '100%' }}
                                />}
                            </div>
                            {!mappedData && <div className='col-2'>
                                <Button
                                    label="Next"
                                    className="p-button-primary"
                                    onClick={async () => {
                                        const mapping = mappingFormRef.current
                                            ? mappingFormRef.current.getMapping()
                                            : dialogData?.mapping;
                                        await requestMappedData(mapping);
                                    }}
                                    style={{ marginTop: '1rem', width: '100%' }}
                                />
                            </div>}
                            {(mappedData /*&& !enrichedData*/) && <>
                                <div className='col-2'>
                                    <Button
                                        label="Enrich top 5 samples"
                                        className="p-button-primary"
                                        style={{ marginTop: '1rem', width: '100%' }}
                                        onClick={(e) => enrichMenu.current.toggle(e)}
                                    />
                                    <Menu model={enrichItems} popup ref={enrichMenu} style={{ minWidth: '300px' }} />
                                </div>
                            </>}
                            {(enrichedData && groupCount !== doneCount && !allRowsHaveRecords) && <>
                                <div className='col-2'>
                                    <Button
                                        label={`Continue (${groupCount - doneCount} of ${groupCount} left)`}
                                        className="p-button-primary"
                                        onClick={runAllEnrichment}
                                        style={{ marginTop: '1rem', width: '100%' }}
                                        disabled={doneCount >= groupCount}
                                    />
                                </div>
                            </>}
                            <div className='col-2'>
                                <Button
                                    label="Close"
                                    className="p-button-secondary"
                                    onClick={() => {
                                        setDialogData(null);
                                        setMappedData(null);
                                        setEnrichedData(null);
                                    }}
                                    style={{ marginTop: '1rem', width: '100%' }}
                                />
                            </div>
                        </>}
                </div>
            </Dialog>
        </>
    );
};